import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { RedirectToSignIn, SignIn, useUser } from "@clerk/clerk-react";
import { Layout } from "@/components/layout/Layout";
import { Feed } from '@/components/feed/Feed';
import { Toaster } from "@/components/ui/toaster";
import { useCardCreation } from "@/hooks/useCardCreation";
import { useEffect } from "react";
import { CreateCard } from '@/routes/CreateCardRoute';
import { Profile } from "@/components/profile/Profile"
import BlueprintBuilder from '@/routes/BlueprintBuilderRoute';
import { BlueprintList } from "@/components/blueprints/BlueprintList"
import { ProfileSetup } from './components/profile/ProfileSetup'
import { DeckListRoute } from "@/routes/DeckListRoute"
import { DeckBuilder } from "@/routes/DeckBuilderRoute"
import { DeckDetailRoute } from "@/routes/DeckDetailRoute"
import { GameGenesisRoute } from '@/routes/GameGenesisRoute';
import { BreakpointProvider } from "@/providers/BreakpointProvider";
import { ThemeProvider } from "@/components/theme/theme-provider"
import { LoadingScreen } from '@/components/loading/LoadingScreen';
import { StyleGuide } from "@/components/StyleGuide";
import { PricingRoute } from "./routes/PricingRoute"

// Protected route using Clerk
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isLoaded, isSignedIn } = useUser();

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  return <>{children}</>;
};

// Layout component using Clerk
const ProtectedLayout = ({ children }: { children: React.ReactNode }) => {
  const { isLoaded, isSignedIn } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { actions } = useCardCreation((path: string) => navigate(path));
  
  if (!isLoaded) {
    return <LoadingScreen />;
  }

  // Don't wrap with Layout if not signed in or on auth pages
  if (!isSignedIn || ['/sign-in', '/sign-up'].includes(location.pathname)) {
    return <>{children}</>;
  }

  return <Layout onCreateCard={() => actions.startCardCreation()}>{children}</Layout>;
};

function App() {
  const { isLoaded } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, actions } = useCardCreation((path: string) => navigate(path));

  useEffect(() => {
    if (location.pathname === '/' && state.isCreatingCard) {
      actions.handleBack();
    }
  }, [location.pathname]);

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider>
      <BreakpointProvider>
        <div id="app" className="bg-background min-h-dvh">
          <ProtectedLayout>
            <Routes>
              <Route 
                path="/sign-in/*" 
                element={
                  <div  className="flex items-center justify-center min-h-screen bg-background">
                    <SignIn 
                      routing="path" 
                      path="/sign-in"
                      afterSignInUrl="/"
                    />
                  </div>
                } 
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Navigate to="/create-game" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/feed"
                element={
                  <ProtectedRoute>
                    <Feed onCreateCard={() => actions.startCardCreation()} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create"
                element={
                  <ProtectedRoute>
                    <CreateCard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/:username"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/setup"
                element={
                  <ProtectedRoute>
                    <ProfileSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/blueprints"
                element={
                  <ProtectedRoute>
                    <BlueprintList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/blueprints/create"
                element={
                  <ProtectedRoute>
                    <BlueprintBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/blueprints/edit/:id"
                element={
                  <ProtectedRoute>
                    <BlueprintBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/decks"
                element={
                  <ProtectedRoute>
                    <DeckListRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/decks/create"
                element={
                  <ProtectedRoute>
                    <DeckBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/decks/:id"
                element={
                  <ProtectedRoute>
                    <DeckDetailRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-game"
                element={
                  <ProtectedRoute>
                    <GameGenesisRoute />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/style-guide" 
                element={
                  <ProtectedRoute>
                    <StyleGuide />
                  </ProtectedRoute>
                } 
              />
              <Route path="/pricing" element={<PricingRoute />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster />
          </ProtectedLayout>
        </div>
      </BreakpointProvider>
    </ThemeProvider>
  );
}

export default App; 